import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { CALENDAR_CONSTANTS } from '../utils/constants';
import { getTimeRange } from '../utils/timeHelpers';

const CalendarContext = createContext(null);

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within CalendarProvider');
  }
  return context;
};

export const CalendarProvider = ({
  children,
  trainerId = null,
  schoolId = null,
  workingHours = [],
  features = {},
  onEventClick,
  onSlotClick,
  onEventChange,
}) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const defaultFeatures = {
    slotSelection: false,
    lockMode: false,
    multiSelect: false,
    showCurrentTime: true,
    ...features,
  };

  const timeRange = useMemo(() => {
    return getTimeRange(workingHours);
  }, [workingHours]);

  const hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT;

  const selectEvent = useCallback((event) => {
    setSelectedEvent(event);
    if (onEventClick) {
      onEventClick(event);
    }
  }, [onEventClick]);

  const clearSelection = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const handleSlotClick = useCallback((slotInfo) => {
    if (onSlotClick) {
      onSlotClick(slotInfo);
    }
  }, [onSlotClick]);

  const handleEventChange = useCallback((event, changes) => {
    if (onEventChange) {
      onEventChange(event, changes);
    }
  }, [onEventChange]);

  const value = useMemo(() => ({
    trainerId,
    schoolId,
    workingHours,
    features: defaultFeatures,

    timeRange,
    hourHeight,

    selectedEvent,
    hoveredEvent,
    isLoading,

    setSelectedEvent,
    setHoveredEvent,
    setIsLoading,

    selectEvent,
    clearSelection,
    handleSlotClick,
    handleEventChange,
  }), [
    trainerId,
    schoolId,
    workingHours,
    defaultFeatures,
    timeRange,
    hourHeight,
    selectedEvent,
    hoveredEvent,
    isLoading,
    selectEvent,
    clearSelection,
    handleSlotClick,
    handleEventChange,
  ]);

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};

export default CalendarContext;
